import { useState, useEffect, useCallback } from 'react';
import debounce from '../Utils/debouncer.js';

/**
 * Force to re-render the component.
 */
function useForceUpdate() {
    const [, updateState] = useState();
    return useCallback(() => updateState({}), []);
}
function observeMutationAndNotify(modalElement, onMutation) {
    if (!window.MutationObserver || !modalElement)
        return () => { };
    const observer = new window.MutationObserver(debounce(onMutation)); // forced to calc style
    observer.observe(modalElement, {
        attributes: true,
        childList: true,
        subtree: true,
    });
    return () => {
        observer.disconnect();
    };
}
function getSize() {
    return {
        width: window.innerWidth || document.documentElement.clientWidth,
        height: window.innerHeight || document.documentElement.clientHeight,
    };
}
function createResizeWindowCallback(x, y, mouseCoord, setX, setY) {
    return () => {
        const { width, height } = getSize();
        const { width: mouseWidth, height: mouseHeight } = mouseCoord;
        let posX = x;
        let posY = y;
        if (Number.isInteger(posX) && mouseWidth) {
            const posNumberX = posX;
            const posNumberY = posY;
            posX = Math.max(posNumberX + mouseWidth > width ? width - mouseWidth : posNumberX, 0);
            posY = Math.max(posNumberY + mouseHeight > height ? height - mouseHeight : posNumberY, 0);
        }
        setX(posX);
        setY(posY);
    };
}
function handleWindowMouseUp(setDragging) {
    const callbackMouseUp = (e) => {
        setDragging(false);
        e.stopPropagation();
        e.preventDefault();
    };
    document.addEventListener('mouseup', callbackMouseUp);
    return () => {
        document.removeEventListener('mouseup', callbackMouseUp);
    };
}
function handleWindowResize(x, y, mouseCoord, setX, setY) {
    const callback = createResizeWindowCallback(x, y, mouseCoord, setX, setY);
    callback();
    window.addEventListener('resize', callback);
    return () => {
        window.removeEventListener('resize', callback);
    };
}
function handleWindowMove(mouseCoord, setX, setY) {
    const changePosition = (e) => {
        const coord = {
            x: mouseCoord.x || 0,
            y: mouseCoord.y || 0,
            width: mouseCoord.width || 0,
            height: mouseCoord.height || 0,
        };
        const { width, height } = getSize();
        let posX = e.pageX - coord.x;
        let posY = e.pageY - coord.y;
        if (posX < 0) {
            posX = 0;
        }
        else if (posX + coord.width > width) {
            posX = width - coord.width;
        }
        if (posY < 0) {
            posY = 0;
        }
        else if (posY + coord.height > height) {
            posY = height - coord.height;
        }
        setX(posX);
        setY(posY);
    };
    const callbackMouseMove = (e) => {
        changePosition(e);
        e.stopPropagation();
        e.preventDefault();
    };
    document.addEventListener('mousemove', callbackMouseMove);
    return () => {
        document.removeEventListener('mousemove', callbackMouseMove);
    };
}
function executeOnDragging(dragging, effetctAction) {
    if (!dragging)
        return () => { };
    return effetctAction();
}
function handleMouseDown(e, modalContent, onMouseDown) {
    if (e.button !== 0)
        return;
    if (modalContent) {
        onMouseDown({
            x: e.pageX - modalContent.offsetLeft,
            y: e.pageY - modalContent.offsetTop,
            width: modalContent.offsetWidth,
            height: modalContent.offsetHeight + 1,
        });
    }
    e.stopPropagation();
    e.preventDefault();
}
function calcStyle(y, ref, height, diff = 180) {
    if (ref && ref.current && ((y || 0) + ref.current.clientHeight) >= height) {
        return { overflow: 'auto', maxHeight: `${height - diff}px` };
    }
    return { overflow: 'none', maxHeight: 'auto' };
}
/**
 * Change modal position.
 * Handle mouse move and window resize to return the new modal position.
 * Return
 * the new postion (x,y),
 * the onMouseDown callback and dragging flag, and
 * the style attributes for modal content.
 */
function useChangeModalPosition(initialPosition, modalContentRef, modalElementRef, diff) {
    const [x, setX] = useState(initialPosition ? initialPosition.x : undefined);
    const [y, setY] = useState(initialPosition ? initialPosition.y : undefined);
    const [dragging, setDragging] = useState(false);
    const [mouseCoord, setMouseCoord] = useState({});
    const forceUpdate = useForceUpdate();
    useEffect(() => observeMutationAndNotify(modalElementRef.current, forceUpdate), [forceUpdate, modalElementRef]);
    useEffect(() => executeOnDragging(dragging, () => handleWindowResize(x, y, mouseCoord, setX, setY)), [dragging, mouseCoord, x, y]);
    useEffect(() => executeOnDragging(dragging, () => handleWindowMouseUp(setDragging)), [dragging]);
    useEffect(() => executeOnDragging(dragging, () => handleWindowMove(mouseCoord, setX, setY)), [dragging, mouseCoord]);
    const onMouseDown = useCallback((e) => {
        handleMouseDown(e, modalContentRef.current, (coord) => {
            setDragging(true);
            setMouseCoord(coord);
        });
    }, [modalContentRef]);
    return Object.assign(Object.assign({ x,
        y }, calcStyle(y, modalContentRef, getSize().height, diff)), { dragging,
        onMouseDown });
}

export { useChangeModalPosition as default };
